import { router } from '@inertiajs/react'
import { BackofficeLayout } from '@/components/layout/BackofficeLayout'
import { SearchInput } from '@/components/shared/SearchInput'
import { Pagination, PaginationMeta } from '@/components/shared/Pagination'
import { Badge } from '@/components/ui/badge'
import { StageBadge } from '@/components/shared/StageBadge'
import { FileText } from 'lucide-react'
import { formatDate } from '@/lib/utils'

type Registration = {
  id: string
  stage: string
  type_name: string
  type_code: string
  jurisdiction_label: string | null
  business_name: string
  account_name: string
  created_at: string
}

type Props = {
  registrations: Registration[]
  search: string
  pagination: PaginationMeta
}

export default function BackofficeTaxRegistrationsIndex({ registrations, search, pagination }: Props) {
  return (
    <BackofficeLayout
      title="Tax Registrations"
      breadcrumbs={[
        { label: 'Backoffice', href: '/backoffice/dashboard' },
        { label: 'Tax Registrations' },
      ]}
    >
      <div className="mb-8">
        <h1 className="text-xl font-semibold tracking-tight">Tax Registrations</h1>
        <p className="text-sm text-muted-foreground">{pagination.total} total</p>
      </div>

      <SearchInput value={search} placeholder="Search registrations..." />

      <div className="mt-4">
        {registrations.length === 0 ? (
          <div className="border rounded-lg py-16 text-center">
            <FileText className="w-6 h-6 mx-auto mb-3 text-muted-foreground/30" />
            <h3 className="text-sm font-medium text-foreground mb-1">No tax registrations found</h3>
            <p className="text-sm text-muted-foreground">Tax registrations will appear here as users create them.</p>
          </div>
        ) : (
          <div className="border rounded-lg">
            {registrations.map((r, i) => (
              <div
                key={r.id}
                className={`flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors ${i > 0 ? 'border-t' : ''}`}
                onClick={() => router.visit(`/backoffice/tax_registrations/${r.id}`)}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center shrink-0">
                    <FileText className="w-3.5 h-3.5 text-muted-foreground" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground truncate">{r.type_name}</span>
                      <StageBadge stage={r.stage} variant="formation" />
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {r.business_name} · {r.jurisdiction_label || r.type_code} · {r.account_name}
                    </div>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground shrink-0 ml-4">
                  {formatDate(r.created_at)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-4">
        <Pagination pagination={pagination} />
      </div>
    </BackofficeLayout>
  )
}
